import { useWalletStore } from '@/store/wallet';
import { useAppChromeStore } from '@/store/appChrome';
import { useLoginUiStore } from '@/store/loginUi';
import logger from '@/common/utils/logger';

// 不需要登录就可以访问的页面（白名单）
const whiteList = [
  '/pages/home/index',
  '/pages/trade/index',
  '/pages/rank/index',
  '/pages/fund/index',
  '/pages/language/index',
  '/pages/create-market/index',
  '/pages/support/index',
];

// 需要登录才能访问的 tabbar 页面
const authRequiredTabs = ['assets'];

function isWhiteListed(url: string): boolean {
  if (!url) return false;
  // 首页路径可能是 /，特殊处理
  if (url === '/') return whiteList.includes('/');
  return whiteList.some(item => url.startsWith(item));
}

/**
 * 检查并处理需要登录的页面访问
 * @returns {boolean} 是否需要阻止导航
 */
function requireAuth(url: string): boolean {
  const walletStore = useWalletStore();
  if (walletStore.isAuthenticated) {
    return false; // 已登录，不阻止
  }

  if (isWhiteListed(url)) {
    return false; // 在白名单中，不阻止
  }

  logger.debug(`[Auth] Unauthorized access to ${url}, showing login modal.`);
  triggerLogin();
  return true; // 需要阻止
}

/**
 * UniApp 路由拦截器
 */
export function setupAuthInterceptor() {
  const interceptor = {
    invoke(args: { url: string }) {
      if (requireAuth(args.url)) {
        return false; // 阻止跳转
      }
      // 跳转前按目标路由同步 chrome，避免闪一下旧态
      useAppChromeStore().syncFromRoute(args.url);
      return true; // 允许跳转
    },
    fail(err: any) {
      logger.error('[Auth Interceptor] Failed:', err);
    }
  };

  uni.addInterceptor('navigateTo', interceptor);
  uni.addInterceptor('redirectTo', interceptor);
  uni.addInterceptor('reLaunch', interceptor);
  uni.addInterceptor('switchTab', interceptor);

  // 返回后根据栈顶页恢复 chrome
  uni.addInterceptor('navigateBack', {
    success() {
      setTimeout(() => {
        useAppChromeStore().syncFromRoute();
      }, 0);
    },
  });

  logger.info('[Auth] Interceptors have been set up.');
}

/**
 * 检查 tabbar 页面是否需要登录
 * @param tabKey tabbar 的 key (home, market, trade, finance, assets)
 * @returns 是否需要登录才能访问
 */
export function isTabRequiresAuth(tabKey: string): boolean {
  return authRequiredTabs.includes(tabKey);
}

/**
 * 检查是否已登录
 */
export function isUserAuthenticated(): boolean {
  const walletStore = useWalletStore();
  return walletStore.isAuthenticated;
}

/**
 * 触发登录弹窗（全局，不依赖当前页是否有 Layout）
 */
export function triggerLogin(authCode?: string): void {
  try {
    useLoginUiStore().open(authCode);
  } catch {
    // ignore
  }
  uni.$emit('openLoginModal', authCode ? { authCode } : undefined);
}

/**
 * 应用启动时检查登录状态
 * 注意：不再自动弹出登录弹框，由用户主动触发
 */
export function checkAuthOnAppLaunch() {
  const walletStore = useWalletStore();
  
  // 只记录登录状态，不自动弹出登录弹框
  setTimeout(() => {
    if (!walletStore.isAuthenticated) {
      logger.info('[Auth] 用户未登录');
    } else {
      logger.info('[Auth] 用户已登录');
    }
  }, 500);
}
